import React, { useState } from 'react';
import './IngredientEducation.css';
import { ingredientProfiles, checkCompatibility } from '../data/ingredientSystem';

const IngredientEducation = ({ isOpen, onClose, ingredient }) => {
  const [activeTab, setActiveTab] = useState('overview');
  
  if (!isOpen || !ingredient) return null;
  
  const profile = ingredientProfiles[ingredient];
  if (!profile) return null;

  const getTypeColor = (type) => {
    const colors = {
      'hydrator': '#3B82F6',
      'multi-tasker': '#8B5CF6',
      'anti-aging': '#F59E0B',
      'antioxidant': '#10B981',
      'exfoliant': '#EF4444',
      'barrier repair': '#6366F1'
    };
    return colors[type] || '#6B7280';
  };

  const renderCompatibilityInfo = () => {
    const commonIngredients = ['hyaluronic-acid', 'niacinamide', 'retinol', 'vitamin-c', 'salicylic-acid'];
    const compatibilityResults = commonIngredients
      .filter(ing => ing !== ingredient)
      .map(ing => ({
        ingredient: ing,
        name: ingredientProfiles[ing]?.name || ing,
        compatibility: checkCompatibility(ingredient, ing)
      }));

    const compatible = compatibilityResults.filter(r => r.compatibility.compatible && !r.compatibility.conflict);
    const synergistic = compatibilityResults.filter(r => r.compatibility.synergistic);
    const conflicts = compatibilityResults.filter(r => r.compatibility.conflict);

    return (
      <div className="compatibility-section">
        {synergistic.length > 0 && (
          <div className="compatibility-group synergy">
            <h4>✨ Works Great With</h4>
            <div className="ingredient-tags">
              {synergistic.map(item => (
                <span key={item.ingredient} className="ingredient-tag synergy">
                  {item.name}
                </span>
              ))}
            </div>
          </div>
        )}
        
        {compatible.length > 0 && (
          <div className="compatibility-group compatible">
            <h4>✅ Safe to Use With</h4>
            <div className="ingredient-tags">
              {compatible.map(item => (
                <span key={item.ingredient} className="ingredient-tag compatible">
                  {item.name}
                </span>
              ))}
            </div>
          </div>
        )}
        
        {conflicts.length > 0 && (
          <div className="compatibility-group conflict">
            <h4>⚠️ Use Separately From</h4>
            <div className="ingredient-tags">
              {conflicts.map(item => (
                <span key={item.ingredient} className="ingredient-tag conflict">
                  {item.name}
                </span>
              ))}
            </div>
            <p className="conflict-note">
              Use these ingredients at different times (morning vs evening) or on alternate days.
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="ingredient-education-overlay">
      <div className="ingredient-education-modal">
        <div className="modal-header">
          <div className="ingredient-title">
            <h2>{profile.name}</h2>
            <span 
              className="ingredient-type"
              style={{ backgroundColor: getTypeColor(profile.type) }}
            >
              {profile.type}
            </span>
          </div>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="modal-tabs">
          <button 
            className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button 
            className={`tab ${activeTab === 'compatibility' ? 'active' : ''}`}
            onClick={() => setActiveTab('compatibility')}
          >
            Compatibility
          </button>
          <button 
            className={`tab ${activeTab === 'usage' ? 'active' : ''}`}
            onClick={() => setActiveTab('usage')}
          >
            How to Use
          </button>
        </div>

        <div className="modal-content">
          {activeTab === 'overview' && (
            <div className="overview-tab">
              <div className="personality-section">
                <h3>Ingredient Personality</h3>
                <p className="personality-text">{profile.personality}</p>
              </div>
              
              <div className="benefits-section">
                <h3>Key Benefits</h3>
                <ul className="benefits-list">
                  {profile.benefits.map((benefit, index) => (
                    <li key={index}>{benefit}</li>
                  ))}
                </ul>
              </div>
              
              <div className="suitable-section">
                <h3>Best For</h3>
                <div className="suitable-tags">
                  {profile.suitableFor.map((type, index) => (
                    <span key={index} className="suitable-tag">
                      {type === 'all skin types' ? 'All Skin Types' : `${type} skin`}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'compatibility' && (
            <div className="compatibility-tab">
              <h3>Ingredient Interactions</h3>
              <p className="compatibility-intro">
                Understanding how {profile.name.toLowerCase()} works with other ingredients helps you build an effective routine.
              </p>
              {renderCompatibilityInfo()}
            </div>
          )}

          {activeTab === 'usage' && (
            <div className="usage-tab">
              <div className="timing-section">
                <h3>When to Use</h3>
                <div className="timing-info">
                  {profile.timeOfDay.includes('morning') && (
                    <div className="timing-item">
                      <span className="timing-icon">🌅</span>
                      <span>Morning routine</span>
                    </div>
                  )}
                  {profile.timeOfDay.includes('evening') && (
                    <div className="timing-item">
                      <span className="timing-icon">🌙</span>
                      <span>Evening routine</span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="tips-section">
                <h3>Pro Tips</h3>
                <ul className="tips-list">
                  {ingredient === 'retinol' && (
                    <>
                      <li>Start with 2-3 times per week and gradually increase</li>
                      <li>Always use sunscreen the next day</li>
                      <li>Apply to clean, dry skin</li>
                    </>
                  )}
                  {ingredient === 'vitamin-c' && (
                    <>
                      <li>Use in the morning for antioxidant protection</li>
                      <li>Store in a cool, dark place</li>
                      <li>Layer under sunscreen for enhanced protection</li>
                    </>
                  )}
                  {ingredient === 'hyaluronic-acid' && (
                    <>
                      <li>Apply to damp skin for better absorption</li>
                      <li>Follow with a moisturizer to seal in hydration</li>
                      <li>Can be used morning and evening</li>
                    </>
                  )}
                  {ingredient === 'niacinamide' && (
                    <>
                      <li>Great for beginners - very gentle</li>
                      <li>Can be mixed with other serums</li>
                      <li>Use consistently for best results</li>
                    </>
                  )}
                  {ingredient === 'salicylic-acid' && (
                    <>
                      <li>Start slowly to build tolerance</li>
                      <li>Use sunscreen during the day</li>
                      <li>Don't combine with other acids initially</li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IngredientEducation;
